<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/PaginationDetail.vue';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

const props = defineProps({
  customers: {
    type: Array,
    default: () => [],
  },
  meta: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  page: {
    type: Number,
    default: 1,
  },
  pageSize: {
    type: Number,
    default: 20,
  },
  fieldDefinitions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['page-change']);
const { t } = useI18n();

const formatDate = value => {
  if (!value) {
    return '—';
  }
  try {
    return format(parseISO(value), 'dd MMM yyyy HH:mm');
  } catch (error) {
    return value;
  }
};

const columnHelper = createColumnHelper();

const getFieldValue = (row, fieldName) => {
  const field = row.fields?.find(
    item => item.fieldName === fieldName || item.field_name === fieldName
  );
  return field?.value || '—';
};

const baseColumns = () => [
  columnHelper.accessor(row => row, {
    id: 'customer',
    header: () => t('DATA_COLLECTION_REPORTS.TABLE.CUSTOMER'),
    cell: cellProps => {
      const customer = cellProps.getValue();
      return h('div', { class: 'space-y-1' }, [
        h(
          'p',
          { class: 'text-sm font-semibold text-n-slate-12 dark:text-white' },
          customer.name || t('DATA_COLLECTION_REPORTS.TABLE.NO_NAME')
        ),
        h('p', { class: 'text-xs text-n-gray-5 dark:text-n-gray-3' }, [
          customer.email || '—',
          customer.phoneNumber ? ` · ${customer.phoneNumber}` : '',
        ]),
      ]);
    },
    size: 220,
  }),
  columnHelper.accessor('status', {
    header: () => t('DATA_COLLECTION_REPORTS.TABLE.STATUS'),
    cell: cellProps => {
      const value = cellProps.getValue();
      const styles = {
        completed:
          'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200',
        in_progress:
          'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200',
        pending:
          'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-200',
      };
      return h(
        'span',
        {
          class: `inline-flex max-w-full flex-wrap items-center justify-center gap-1 whitespace-normal rounded-full px-3 py-1 text-left text-xs font-semibold ${styles[value] || styles.pending}`,
        },
        t(`DATA_COLLECTION_REPORTS.STATUS.${(value || 'pending').toUpperCase()}`)
      );
    },
  }),
];

const dynamicFieldColumns = fieldDefs =>
  fieldDefs.map(field =>
    columnHelper.accessor(row => getFieldValue(row, field.fieldName), {
      id: `field-${field.id}`,
      header: () => field.displayName || field.fieldName,
      cell: cellProps =>
        h(
          'span',
          { class: 'text-sm text-n-slate-12 dark:text-n-gray-2' },
          cellProps.getValue() || '—'
        ),
      size: 200,
    })
  );

const lastCollectedColumn = columnHelper.accessor('lastCollectedAt', {
  header: () => t('DATA_COLLECTION_REPORTS.TABLE.LAST_COLLECTED'),
  cell: cellProps =>
    h(
      'span',
      { class: 'text-sm text-n-gray-7 dark:text-n-gray-2' },
      formatDate(cellProps.getValue())
    ),
  size: 180,
});

const tableColumns = computed(() => [
  ...baseColumns(),
  ...dynamicFieldColumns(props.fieldDefinitions),
  lastCollectedColumn,
]);

const paginationState = computed(() => ({
  pageIndex: Math.max(props.page - 1, 0),
  pageSize: props.pageSize,
}));

const totalRows = computed(() => props.meta?.total_count || props.customers.length);

const table = useVueTable({
  get data() {
    return props.customers;
  },
  get columns() {
    return tableColumns.value;
  },
  manualPagination: true,
  getCoreRowModel: getCoreRowModel(),
  state: {
    get pagination() {
      return paginationState.value;
    },
  },
  get rowCount() {
    return totalRows.value;
  },
  onPaginationChange: updater => {
    const next = updater(paginationState.value);
    emit('page-change', next.pageIndex + 1);
  },
});
</script>

<template>
  <div class="relative rounded-2xl border border-n-weak bg-white/90 p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/30">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12 dark:text-white">
          {{ $t('DATA_COLLECTION_REPORTS.TABLE.TITLE') }}
        </h3>
        <p class="text-sm text-n-gray-6 dark:text-n-gray-3">
          {{ $t('DATA_COLLECTION_REPORTS.TABLE.SUBTITLE') }}
        </p>
      </div>
    </div>

    <div class="mt-4 space-y-4">
      <div v-if="!isLoading && !customers.length" class="py-8">
        <EmptyState
          :title="$t('DATA_COLLECTION_REPORTS.TABLE.EMPTY_TITLE')"
          :message="$t('DATA_COLLECTION_REPORTS.TABLE.EMPTY_MESSAGE')"
        />
      </div>
      <div v-else>
        <Table :table="table" :borderless="true" minimal />
        <Pagination class="mt-4" :table="table" />
      </div>
    </div>

    <div
      v-if="isLoading"
      class="absolute left-0 top-0 flex h-full w-full items-center justify-center rounded-2xl bg-white/70 dark:bg-slate-900/40"
    >
      <Spinner />
    </div>
  </div>
</template>
